import { api, apiBlob, apiUpload } from '../../../lib/api';
import { ApiError } from '../../../lib/apiError';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CreateEmploymentRecordRequest,
  CreatePersonnelRequest,
  EndEmploymentRecordRequest,
  PersonIdentity,
  PersonPhotoInput,
  LinkableAccount,
  PersonnelCatalogEntry,
  PersonnelCreated,
  PlantillaVacantItem,
  PersonSummary,
  PersonnelCatalogs,
  PersonnelDetail,
  PersonnelListItem,
  SetPersonnelAccessLevelRequest,
  UpdatePersonnelRequest,
  PersonnelDocument,
  PersonnelEducation,
  PersonnelEducationInput,
  PersonnelFamilyMember,
  PersonnelFamilyMemberInput,
  PersonnelFamilySuggestions,
  PersonnelEligibility,
  PersonnelEligibilityInput,
  PersonnelEmploymentRecord,
  PreviousEmploymentInput,
  PersonnelVoluntaryWork,
  PersonnelVoluntaryWorkInput,
  PersonnelTraining,
  PersonnelTrainingInput,
  PersonnelRecognition,
  PersonnelRecognitionInput,
  PersonnelReference,
  PersonnelReferenceInput,
  PersonnelDisciplinaryRecord,
  PersonnelDisciplinaryRecordInput,
  PersonnelSaln,
  PersonnelSalnInput,
  PersonnelSpecialSkill,
  PersonnelSpecialSkillInput,
  PersonnelSurveyAnswer,
  PersonnelSurveyAnswerInput,
} from './personnelTypes';

/** Adds the unit, employment and post facets to the shared contract. */
interface PersonnelQuery extends ListQuery {
  /** Comma-separated org-unit ids. */
  unit?: string;
  /** Comma-separated employment type names. */
  employment?: string;
  /** Comma-separated: `assigned`, `unassigned`. Whether they currently hold a post. */
  post?: string;
}

function toQueryString(query: PersonnelQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Present-but-empty is "none of these" and must survive; a truthiness check drops it and
  // the server reads the axis as unmentioned.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.unit !== undefined) params.set('unit', query.unit);
  if (query.employment !== undefined) params.set('employment', query.employment);
  if (query.post !== undefined) params.set('post', query.post);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const personnelApi = {
  list: (query: PersonnelQuery, signal?: AbortSignal) =>
    api<PagedResult<PersonnelListItem>>(`/api/personnel${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PersonnelDetail>(`/api/personnel/${id}`, { signal }),

  /** The vocabularies every picker on the form draws on. One call, all six. */
  catalogs: (signal?: AbortSignal) => api<PersonnelCatalogs>('/api/personnel/catalogs', { signal }),

  /** Every vacant post, for the merged picker on the employment step. */
  vacantItems: (signal?: AbortSignal) =>
    api<PlantillaVacantItem[]>('/api/plantilla/vacant-items', { signal }),

  /**
   * Adds an HMO provider the reader typed because it was missing.
   *
   * Its own key — `personnel.manage-catalogs` — because a row here changes what every future
   * record can say. Idempotent on the name, so two readers adding the same provider at once
   * get one row and no error.
   */
  addHmoProvider: (name: string) =>
    api<PersonnelCatalogEntry>('/api/personnel/catalogs/hmo-providers', {
      method: 'POST',
      body: { name },
    }),

  /**
   * Adds a nature of appointment the reader typed because it was missing.
   *
   * Same key and same shape as the provider above: a row here changes what every future
   * service record can say, which is a different privilege from filling one in.
   */
  addAppointmentNature: (name: string) =>
    api<PersonnelCatalogEntry>('/api/personnel/catalogs/appointment-natures', {
      method: 'POST',
      body: { name },
    }),

  /**
   * Adds a special-skill category the reader typed because it was missing.
   *
   * Same key and same shape as the two above. CS Form 212 item 31 classifies this section not
   * at all, so the six seeded rows are a starting vocabulary rather than the set — a reader who
   * needs "Language" adds it from the form that wanted it.
   */
  addSpecialSkillCategory: (name: string) =>
    api<PersonnelCatalogEntry>('/api/personnel/catalogs/special-skill-categories', {
      method: 'POST',
      body: { name },
    }),

  /** Accounts with no employee attached, for the link mode on the Account step. */
  linkableAccounts: (signal?: AbortSignal) =>
    api<LinkableAccount[]>('/api/personnel/linkable-accounts', { signal }),

  /**
   * Creates the record — and the account, when the Account step asked for one.
   *
   * The response carries the issued credential exactly once: it is never persisted in clear
   * text and never readable again, so whatever the UI does with it, it does now.
   */
  create: (body: CreatePersonnelRequest) =>
    api<PersonnelCreated>('/api/personnel', { method: 'POST', body }),

  update: (id: string, body: UpdatePersonnelRequest) =>
    api<PersonnelDetail>(`/api/personnel/${id}`, { method: 'PUT', body }),

  /**
   * Classifies the record, or clears its classification.
   *
   * Its own endpoint and its own key: a PUT rewrites every column it carries, so a level on
   * the update request would be sent back by every reader who edits anything — and a reader
   * without `personnel.set-access-level` would have ordinary edits refused.
   */
  setAccessLevel: (id: string, body: SetPersonnelAccessLevelRequest) =>
    api<PersonnelDetail>(`/api/personnel/${id}/access-level`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`/api/personnel/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/personnel/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/personnel/clear', { method: 'POST' }),

  /*
    Employment history, a post HERE — its own operations under personnel.assign, because
    placing someone in a post writes rows the employee's own record does not name.
  */
  addEmploymentRecord: (id: string, body: CreateEmploymentRecordRequest) =>
    api<void>(`/api/personnel/${id}/employment-records`, { method: 'POST', body }),

  endEmploymentRecord: (id: string, employmentRecordId: string, body: EndEmploymentRecordRequest) =>
    api<void>(`/api/personnel/${id}/employment-records/${employmentRecordId}/end`, {
      method: 'POST',
      body,
    }),

  /*
    Educational background. All four are personnel.update: recording where someone studied is
    correcting their record, not the separate decision that earned personnel.assign its own key.
  */
  addEducation: (id: string, body: PersonnelEducationInput) =>
    api<PersonnelEducation>(`/api/personnel/${id}/education`, { method: 'POST', body }),

  updateEducation: (id: string, educationId: string, body: PersonnelEducationInput) =>
    api<PersonnelEducation>(`/api/personnel/${id}/education/${educationId}`, {
      method: 'PUT',
      body,
    }),

  deleteEducation: (id: string, educationId: string) =>
    api<void>(`/api/personnel/${id}/education/${educationId}`, { method: 'DELETE' }),

  restoreEducation: (id: string, educationId: string) =>
    api<void>(`/api/personnel/${id}/education/${educationId}/restore`, { method: 'POST' }),

  /*
    Family background. The REQUEST carries names, the RESPONSE does not: the name goes to the
    identity store and only a personId comes back. Resolving it for display is
    `personsApi.resolve`, under its own key.
  */
  addFamilyMember: (id: string, body: PersonnelFamilyMemberInput) =>
    api<PersonnelFamilyMember>(`/api/personnel/${id}/family`, { method: 'POST', body }),

  updateFamilyMember: (id: string, familyMemberId: string, body: PersonnelFamilyMemberInput) =>
    api<PersonnelFamilyMember>(`/api/personnel/${id}/family/${familyMemberId}`, {
      method: 'PUT',
      body,
    }),

  deleteFamilyMember: (id: string, familyMemberId: string) =>
    api<void>(`/api/personnel/${id}/family/${familyMemberId}`, { method: 'DELETE' }),

  restoreFamilyMember: (id: string, familyMemberId: string) =>
    api<void>(`/api/personnel/${id}/family/${familyMemberId}/restore`, { method: 'POST' }),

  /** Occupations and employers already on file — suggestions, not a vocabulary. */
  familySuggestions: (signal?: AbortSignal) =>
    api<PersonnelFamilySuggestions>('/api/personnel/family/suggestions', { signal }),

  /* Earned eligibilities. personnel.update, not eligibilities.* — that governs the catalog. */
  addEligibility: (id: string, body: PersonnelEligibilityInput) =>
    api<PersonnelEligibility>(`/api/personnel/${id}/eligibility`, { method: 'POST', body }),

  updateEligibility: (id: string, eligibilityId: string, body: PersonnelEligibilityInput) =>
    api<PersonnelEligibility>(`/api/personnel/${id}/eligibility/${eligibilityId}`, {
      method: 'PUT',
      body,
    }),

  deleteEligibility: (id: string, eligibilityId: string) =>
    api<void>(`/api/personnel/${id}/eligibility/${eligibilityId}`, { method: 'DELETE' }),

  restoreEligibility: (id: string, eligibilityId: string) =>
    api<void>(`/api/personnel/${id}/eligibility/${eligibilityId}/restore`, {
      method: 'POST',
    }),

  /*
    Survey answers. personnel.update, not survey-questions.* — that governs the questionnaire.

    `delete` and `restore` have NO caller today, and that is a stated gap rather than dead code:
    the Survey panel lists one row per question, so there is no row to remove — an answer is
    corrected in place and cannot be cleared back to unanswered. The endpoints are live and
    tested, and are what a Clear-answer affordance would call when one is built.
  */
  addSurveyAnswer: (id: string, body: PersonnelSurveyAnswerInput) =>
    api<PersonnelSurveyAnswer>(`/api/personnel/${id}/survey`, { method: 'POST', body }),

  updateSurveyAnswer: (id: string, answerId: string, body: PersonnelSurveyAnswerInput) =>
    api<PersonnelSurveyAnswer>(`/api/personnel/${id}/survey/${answerId}`, {
      method: 'PUT',
      body,
    }),

  deleteSurveyAnswer: (id: string, answerId: string) =>
    api<void>(`/api/personnel/${id}/survey/${answerId}`, { method: 'DELETE' }),

  restoreSurveyAnswer: (id: string, answerId: string) =>
    api<void>(`/api/personnel/${id}/survey/${answerId}/restore`, { method: 'POST' }),

  /*
    Employment history, a PREVIOUS employer — personnel.update. No catalog is consulted; every
    value is stored as stated. The server refuses (409) a post here on every by-row route.
  */
  addPreviousEmployment: (id: string, body: PreviousEmploymentInput) =>
    api<PersonnelEmploymentRecord>(`/api/personnel/${id}/employment-records/previous`, {
      method: 'POST',
      body,
    }),

  updatePreviousEmployment: (id: string, employmentRecordId: string, body: PreviousEmploymentInput) =>
    api<PersonnelEmploymentRecord>(`/api/personnel/${id}/employment-records/${employmentRecordId}`, {
      method: 'PUT',
      body,
    }),

  deletePreviousEmployment: (id: string, employmentRecordId: string) =>
    api<void>(`/api/personnel/${id}/employment-records/${employmentRecordId}`, { method: 'DELETE' }),

  restorePreviousEmployment: (id: string, employmentRecordId: string) =>
    api<void>(`/api/personnel/${id}/employment-records/${employmentRecordId}/restore`, {
      method: 'POST',
    }),

  /* Voluntary work. No catalog is consulted — every value on the row is stored as stated. */
  addVoluntaryWork: (id: string, body: PersonnelVoluntaryWorkInput) =>
    api<PersonnelVoluntaryWork>(`/api/personnel/${id}/voluntary-work`, { method: 'POST', body }),

  updateVoluntaryWork: (id: string, voluntaryWorkId: string, body: PersonnelVoluntaryWorkInput) =>
    api<PersonnelVoluntaryWork>(`/api/personnel/${id}/voluntary-work/${voluntaryWorkId}`, {
      method: 'PUT',
      body,
    }),

  deleteVoluntaryWork: (id: string, voluntaryWorkId: string) =>
    api<void>(`/api/personnel/${id}/voluntary-work/${voluntaryWorkId}`, { method: 'DELETE' }),

  restoreVoluntaryWork: (id: string, voluntaryWorkId: string) =>
    api<void>(`/api/personnel/${id}/voluntary-work/${voluntaryWorkId}/restore`, {
      method: 'POST',
    }),

  /*
    Learning and development. personnel.update, not competencies.* — that governs the framework
    every 201 file is measured against, where these record that one employee attended one
    course. The record number is assigned by the server and never sent.
  */
  addTraining: (id: string, body: PersonnelTrainingInput) =>
    api<PersonnelTraining>(`/api/personnel/${id}/training`, { method: 'POST', body }),

  updateTraining: (id: string, trainingId: string, body: PersonnelTrainingInput) =>
    api<PersonnelTraining>(`/api/personnel/${id}/training/${trainingId}`, {
      method: 'PUT',
      body,
    }),

  deleteTraining: (id: string, trainingId: string) =>
    api<void>(`/api/personnel/${id}/training/${trainingId}`, { method: 'DELETE' }),

  restoreTraining: (id: string, trainingId: string) =>
    api<void>(`/api/personnel/${id}/training/${trainingId}/restore`, { method: 'POST' }),

  /*
    Special skills and hobbies. personnel.update, like every other section of the 201 file —
    recording that somebody plays the violin is a correction to their own record. Choosing a
    category is covered by the same key; ADDING one goes to the catalog endpoint above.
  */
  addSpecialSkill: (id: string, body: PersonnelSpecialSkillInput) =>
    api<PersonnelSpecialSkill>(`/api/personnel/${id}/special-skills`, { method: 'POST', body }),

  updateSpecialSkill: (id: string, specialSkillId: string, body: PersonnelSpecialSkillInput) =>
    api<PersonnelSpecialSkill>(`/api/personnel/${id}/special-skills/${specialSkillId}`, {
      method: 'PUT',
      body,
    }),

  deleteSpecialSkill: (id: string, specialSkillId: string) =>
    api<void>(`/api/personnel/${id}/special-skills/${specialSkillId}`, { method: 'DELETE' }),

  restoreSpecialSkill: (id: string, specialSkillId: string) =>
    api<void>(`/api/personnel/${id}/special-skills/${specialSkillId}/restore`, { method: 'POST' }),

  // Recognition — CS Form 212, section VIII, item 32. `personnel.update` on all four.
  addRecognition: (id: string, body: PersonnelRecognitionInput) =>
    api<PersonnelRecognition>(`/api/personnel/${id}/recognitions`, { method: 'POST', body }),

  updateRecognition: (id: string, recognitionId: string, body: PersonnelRecognitionInput) =>
    api<PersonnelRecognition>(`/api/personnel/${id}/recognitions/${recognitionId}`, {
      method: 'PUT',
      body,
    }),

  deleteRecognition: (id: string, recognitionId: string) =>
    api<void>(`/api/personnel/${id}/recognitions/${recognitionId}`, { method: 'DELETE' }),

  restoreRecognition: (id: string, recognitionId: string) =>
    api<void>(`/api/personnel/${id}/recognitions/${recognitionId}/restore`, { method: 'POST' }),

  /*
    Character references. `personnel.update` on all four — the request carries a name the caller
    already knows, which is not a reveal, and the response carries none.
  */
  /**
   * One government-issued number, read back from the identity store.
   *
   * `personnel.reveal`, ONE card at a time, and every call is audited. There is no batch form,
   * for the reason there is no bulk reveal of a date of birth — a caller wanting five numbers
   * makes five calls and leaves five rows in the trail.
   */
  revealIdentificationNumber: (id: string, identificationId: string, signal?: AbortSignal) =>
    api<string>(`/api/personnel/${id}/identifications/${identificationId}/number`, { signal }),

  addReference: (id: string, body: PersonnelReferenceInput) =>
    api<PersonnelReference>(`/api/personnel/${id}/references`, { method: 'POST', body }),

  updateReference: (id: string, referenceId: string, body: PersonnelReferenceInput) =>
    api<PersonnelReference>(`/api/personnel/${id}/references/${referenceId}`, {
      method: 'PUT',
      body,
    }),

  deleteReference: (id: string, referenceId: string) =>
    api<void>(`/api/personnel/${id}/references/${referenceId}`, { method: 'DELETE' }),

  restoreReference: (id: string, referenceId: string) =>
    api<void>(`/api/personnel/${id}/references/${referenceId}/restore`, { method: 'POST' }),

  /*
    SALN filings. FOUR CALLS, not sixteen — a filing is one transaction, so its four schedules
    travel with it and a correction replaces them wholesale. A schedule line carries no id on
    the wire for one of them to be addressed by.
  */

  addSaln: (id: string, body: PersonnelSalnInput) =>
    api<PersonnelSaln>(`/api/personnel/${id}/salns`, { method: 'POST', body }),

  updateSaln: (id: string, salnId: string, body: PersonnelSalnInput) =>
    api<PersonnelSaln>(`/api/personnel/${id}/salns/${salnId}`, { method: 'PUT', body }),

  deleteSaln: (id: string, salnId: string) =>
    api<void>(`/api/personnel/${id}/salns/${salnId}`, { method: 'DELETE' }),

  restoreSaln: (id: string, salnId: string) =>
    api<void>(`/api/personnel/${id}/salns/${salnId}/restore`, { method: 'POST' }),

  /* Disciplinary and administrative records. `personnel.update`, like every other section. */

  addDisciplinaryRecord: (id: string, body: PersonnelDisciplinaryRecordInput) =>
    api<PersonnelDisciplinaryRecord>(`/api/personnel/${id}/disciplinary-records`, {
      method: 'POST',
      body,
    }),

  updateDisciplinaryRecord: (
    id: string,
    recordId: string,
    body: PersonnelDisciplinaryRecordInput,
  ) =>
    api<PersonnelDisciplinaryRecord>(`/api/personnel/${id}/disciplinary-records/${recordId}`, {
      method: 'PUT',
      body,
    }),

  deleteDisciplinaryRecord: (id: string, recordId: string) =>
    api<void>(`/api/personnel/${id}/disciplinary-records/${recordId}`, { method: 'DELETE' }),

  restoreDisciplinaryRecord: (id: string, recordId: string) =>
    api<void>(`/api/personnel/${id}/disciplinary-records/${recordId}/restore`, {
      method: 'POST',
    }),

  /**
   * Files one document against one checklist row. Through `apiUpload` — multipart, real progress.
   *
   * SEVERAL may name one checklist row, so this never replaces: an employee holds one birth
   * certificate and ten appointments.
   */
  addDocument: (
    id: string,
    documentTypeId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<PersonnelDocument>(`/api/personnel/${id}/documents/${documentTypeId}`, form, {
      onProgress,
    });
  },

  /**
   * Swaps one filed document's file, keeping the row and its kind. The superseded file is
   * soft-removed server-side, its bytes kept.
   */
  replaceDocument: (
    id: string,
    documentId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<PersonnelDocument>(`/api/personnel/${id}/documents/${documentId}/replace`, form, {
      onProgress,
    });
  },

  /** Soft — the row and the bytes are both kept, so the restore is faithful. */
  deleteDocument: (id: string, documentId: string) =>
    api<void>(`/api/personnel/${id}/documents/${documentId}`, { method: 'DELETE' }),

  restoreDocument: (id: string, documentId: string) =>
    api<void>(`/api/personnel/${id}/documents/${documentId}/restore`, { method: 'POST' }),

  /**
   * Fetches one filed document. A FETCH then an object URL, never an `<a href>` at the
   * endpoint: the token travels in a header no navigation sends.
   */
  downloadDocument: (id: string, documentId: string, signal?: AbortSignal) =>
    apiBlob(`/api/personnel/${id}/documents/${documentId}/file`, { signal }),
};

/**
 * The identity store's own surface.
 *
 * Separate from `personnelApi` because it is a separate system in everything but deployment:
 * a different database, a different login, and — when the satellite is built — a different
 * base URL. Keeping the calls in their own object is what makes that change one file.
 */
export const personsApi = {
  /**
   * Turns a page of ids into display names, in ONE call.
   *
   * Never call this per row. The server caps the batch at 100 and refuses a longer one, which
   * is the control that stops a leaked token walking the table — a per-row caller would be
   * doing exactly what the cap exists to prevent, one request at a time.
   */
  resolve: (personIds: string[], signal?: AbortSignal) =>
    api<PersonSummary[]>('/api/persons/resolve', {
      method: 'POST',
      body: { personIds },
      signal,
    }),

  /** `personnel.reveal`. One record, audited on every call, successful or not. */
  reveal: (personId: string, signal?: AbortSignal) =>
    api<PersonIdentity>(`/api/persons/${personId}/reveal`, { signal }),

  /** Writes or replaces the photograph. Keyed by PersonId, so it follows the create. */
  setPhoto: (personId: string, body: PersonPhotoInput) =>
    api<void>(`/api/persons/${personId}/photo`, { method: 'PUT', body }),

  removePhoto: (personId: string) =>
    api<void>(`/api/persons/${personId}/photo`, { method: 'DELETE' }),

  /**
   * The photograph itself, as a Blob.
   *
   * **Not a URL.** The endpoint carries `personnel.read` and this client authenticates with
   * a bearer token in a header, which an `<img src>` cannot send — and the API has its own
   * origin, so a bare path would not reach it either. `usePersonPhoto` turns what comes back
   * into an object URL for the tab that asked.
   *
   * A record with no photograph resolves to null rather than throwing: not having one is the
   * ordinary case, and an avatar falling back to initials is the correct rendering of it.
   */
  fetchPhoto: async (personId: string, signal?: AbortSignal): Promise<Blob | null> => {
    try {
      const { blob } = await apiBlob(`/api/persons/${personId}/photo`, { signal });

      return blob;
    } catch (caught) {
      if (caught instanceof ApiError && caught.isNotFound) {
        return null;
      }

      throw caught;
    }
  },

  /** Destroys the four values. Permanent, and refused unless the person is already deleted. */
  erase: (personId: string) => api<void>(`/api/persons/${personId}/erase`, { method: 'POST' }),
};
